import OSS from 'ali-oss';
import ExifReader from 'exifreader';
import { $http, $api } from '@/apiService/httpApi';

export default class OssService {
  constructor() {
    this.client = null;
    this.path = '';
    this.errorObj = {
      RequestTimeTooSkewedError: '时间不对，请校准电脑时间',
      CallbackFailedError: '回调参数设置错误或参数格式错误',
      RequestTimeoutError: '请求超时',
      NotImplementedError: '无法处理',
      AccessDeniedError: '无指定操作的权限',
      InvalidAccessKeyIdError: '可能是AccessKeyID禁用或不存在',
      ImageDamageError: '图片损坏',
      AccessForbiddenError: '禁止访问',
      UserDisableError: '被禁用',
    };
  }
  /**
   * 初始化 OSS 客户端
   * @returns 初始化 OSS 客户端的 Promise 对象
   */
  async initOSS() {
    try {
      let res = await this.getSTSToken();
      let { accessKeyId, accessKeySecret, securityToken, bucket, endpoint } = res;
      this.client = new OSS({
        accessKeyId,
        accessKeySecret,
        stsToken: securityToken,
        bucket,
        endpoint,
        secure: true,
      });
    } catch (error) {
      let msg = this.errorObj[error.name] ? this.errorObj[error.name] : error.name;
      msg = msg || 'OSS初始化失败';
      $api.toast(msg, 'error');
    }
  }
  /**
   * 获取STS Token
   * @returns 返回STS Token，若获取失败则返回null
   */
  async getSTSToken() {
    let res = await $http.post('ossConfig/getStsOssToken');
    if (res.code === 0) {
      this.path = res.result.path;
      return res.result;
    } else {
      $api.toast(res.msg, 'error');
      return { accessKeyId: '', accessKeySecret: '', stsToken: '' };
    }
  }
  /**
   * 文件上传 OSS
   * @param {*} param0
   * @param {*} count
   * @returns
   */
  async multipartUploadRefreshToken({ ossFileName, file, obj }, count = 0) {
    let result = { code: 0, msg: '上传成功' };
    try {
      await this.client.multipartUpload(ossFileName, file, obj);
    } catch (error) {
      if (count < 3) {
        // 重新获取STS Token，所有的错误都重试3次
        await this.initOSS();
        return await this.multipartUploadRefreshToken({ ossFileName, file, obj }, ++count);
      } else {
        result.code = 1;
        result.msg = this.errorObj[error.name] ? this.errorObj[error.name] : error.name;
      }
    }
    return result;
  }
  /**
   * 获取签名URL
   * @param {*} param0
   * @param {*} count
   * @returns
   */
  async signatureUrlRefreshToken({ fileName, params }, count = 0) {
    let result = '';
    try {
      result = this.client.signatureUrl(fileName, { ...params });
    } catch (error) {
      if (count < 3) {
        // 重新获取STS Token，所有的错误都重试3次
        await this.initOSS();
        return await this.signatureUrlRefreshToken({ fileName, params }, ++count);
      }
    }
    return result;
  }
  /**
   * 上传图片
   * @param {*} file
   * @param {*} progressCallBack
   * @returns
   */
  async uploadFile(file, progressCallBack) {
    let fileName = file.name;
    let ossFileName = `${this.path}/${moment().format('YYYYMMDDHHmmssSSS')}${Math.round(Math.random() * 100)}/${fileName}`;
    if (encodeURIComponent(fileName).length > 350) {
      $api.toast('图片名称过长', 'warning');
      throw new Error('图片名称过长');
    }
    let result = { code: 0, msg: '上传成功', url: '', ossFileName };
    const res1 = await this.multipartUploadRefreshToken({
      ossFileName,
      file,
      obj: {
        progress: p => {
          return function (done) {
            if (typeof progressCallBack === 'function') {
              progressCallBack(p);
            }
            done();
          };
        },
      },
    });
    result.code = res1.code;
    result.msg = res1.msg;
    if (res1.code == 0) {
      let res2 = await this.signatureUrlRefreshToken({ fileName: ossFileName, params: { expires: 31536000000 } });
      if (res2) {
        res2 = res2.substring(0, res2.indexOf('?')); // 去除地址上面的参数
        res2 = res2.replace(/oss-cn-(?:hangzhou|shanghai).aliyuncs/g, 'mwdwz'); // 替换短地址
        result.url = res2;
      } else {
        result.code = 1;
        result.msg = '获取文件URL失败';
      }
    }
    if (result.code === 1) $api.toast(result.msg, 'warning');
    return result;
  }
  /**
   * 上传文件，带图片的拍摄时间
   * @param {*} file
   * @param {*} progressCallBack
   * @returns
   */
  async uploadFileAddDate(file, progressCallBack) {
    // 图片处理
    let imgDate = await this.getFileExIfData(file);
    let fileName = file.name;
    let ossFileName = `${this.path}/${moment().format('YYYYMMDDHHmmssSSS')}${Math.round(Math.random() * 100)}/${fileName}`;
    if (encodeURIComponent(fileName).length > 350) {
      $api.toast('图片名称过长', 'warning');
      throw new Error('图片名称过长');
    }
    let result = { code: 0, msg: '上传成功', url: '', ossFileName, imgDate };
    const res = await this.multipartUploadRefreshToken({
      ossFileName,
      file,
      obj: {
        progress: p => {
          return function (done) {
            if (typeof progressCallBack === 'function') {
              progressCallBack(p);
            }
            done();
          };
        },
      },
    });
    if (res.code == 0) {
      result.url = await this.signatureUrlRefreshToken({ fileName: ossFileName, params: { expires: 31536000000 } });
      result.url = result.url.substring(0, result.url.indexOf('?')); // 去除地址上面的参数
      result.url = result.url.replace(/oss-cn-(?:hangzhou|shanghai).aliyuncs/g, 'mwdwz'); // 替换短地址
    } else {
      $api.toast(res.msg, 'warning');
    }
    return result;
  }
  /**
   * 获取上传文件的信息
   * @param {*} file
   * @returns
   */
  async getFileExIfData(file) {
    try {
      let tags = await ExifReader.load(file);
      return tags['DateTimeOriginal'].description;
    } catch (error) {
      return '';
    }
  }
  /**
   * 添加水印
   * @param {*} file
   * @param {*} date
   * @returns
   */
  editWaterMark(file, date) {
    return new Promise((resolve, reject) => {
      try {
        let fileReader = new FileReader();
        fileReader.readAsDataURL(file);
        let canvas = document.createElement('canvas');
        let ctx = canvas.getContext('2d');
        let img = document.createElement('img');
        fileReader.onload = function () {
          img.src = fileReader.result;
          img.onload = function () {
            canvas.width = img.width;
            canvas.height = img.height;
            ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
            ctx.font = '18px';
            ctx.fillText(date, canvas.width - 100, canvas.height - 50);
            let imgData = canvas.toDataURL(file.type),
              arr = imgData.split(','),
              mime = arr[0].match(/:(.*?);/)[1],
              bstr = atob(arr[1]),
              n = bstr.length,
              u8arr = new Uint8Array(n);
            while (n--) {
              u8arr[n] = bstr.charCodeAt(n);
            }
            let newFile = new File([u8arr], file.name, { type: mime });
            resolve(newFile);
          };
        };
      } catch (error) {
        reject('');
        throw new Error(error);
      }
    });
  }
}
